import { ModelInfo } from '../../ModelPreviewComponents';

/**
 * Extended dimensions data from storage
 */
export interface ExtendedDimensionsData {
  dimensions: { x: number; y: number; z: number };
  scale: number;
  trueOriginalDimensions: { x: number; y: number; z: number };
}

/**
 * Get extended dimensions data from localStorage
 * @param modelId - The model ID to look up
 * @returns Extended dimensions data or null if not found
 */
export const getExtendedDimensionsFromStorage = (modelId: string): ExtendedDimensionsData | null => {
  try {
    const storageKey = `model_dimensions_${modelId}`;
    const savedDataString = localStorage.getItem(storageKey);
    
    if (!savedDataString) {
      return null;
    }
    
    try {
      const savedData = JSON.parse(savedDataString);
      if (savedData && savedData.dimensions) {
        return {
          dimensions: savedData.dimensions,
          scale: savedData.scale || 1.0,
          trueOriginalDimensions: savedData.trueOriginalDimensions || savedData.dimensions
        };
      } else {
        return null;
      }
    } catch (e) {
      // Try to clean up corrupted data
      try {
        localStorage.removeItem(storageKey);
      } catch (cleanupError) { /* Ignore cleanup errors */ }
      return null;
    }
  } catch (e) {
    return null;
  }
};

/**
 * Save model changes to persistent storage
 * @param modelToSave - The model to save
 * @param currentDimensions - Current dimensions state
 */
export const saveModelChanges = async (
  modelToSave: ModelInfo, 
  currentDimensions: { x: number; y: number; z: number } | null
): Promise<void> => {
  try {
    // Current model dimensions
    const dimensions = modelToSave.versions?.[modelToSave.activeVersionIndex || 0]?.dimensions || currentDimensions;
    const currentScale = modelToSave.versions?.[modelToSave.activeVersionIndex || 0]?.scale || 1.0;
    
    if (!dimensions) {
      return;
    }
    
    // Save directly to localStorage
    const storageKey = `model_dimensions_${modelToSave.id}`;
    
    // First check if we already have data to preserve the trueOriginalDimensions
    let trueOriginalDimensions = modelToSave.originalDimensions || dimensions;
    try {
      const existingDataString = localStorage.getItem(storageKey);
      if (existingDataString) {
        const existingData = JSON.parse(existingDataString);
        if (existingData && existingData.trueOriginalDimensions) {
          trueOriginalDimensions = existingData.trueOriginalDimensions;
        }
      }
    } catch (e) {
      // If there's an error reading existing data, just use the current original dimensions
    }
    
    const saveData = {
      dimensions: dimensions,
      scale: currentScale,
      timestamp: new Date().toISOString(),
      modelId: modelToSave.id,
      trueOriginalDimensions: trueOriginalDimensions
    };
    
    // First clear the existing item to ensure a clean save
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      // Ignore any errors on removal
    }
    
    // Now save the new data
    try {
      const dataString = JSON.stringify(saveData);
      localStorage.setItem(storageKey, dataString);
    } catch (e) {
      // Ignore any storage errors
    }
  } catch (error) {
    // Ignore any saving errors
  }
};

/**
 * Reset model dimensions to original values
 * @param model - The model to reset
 * @param setDimensions - State setter for dimensions
 */
export const resetOriginalValues = (
  model: ModelInfo,
  setDimensions: React.Dispatch<React.SetStateAction<{ x: number; y: number; z: number } | null>>
): void => {
  if (!model.originalDimensions) {
    return;
  }
  
  // Get the true original dimensions from storage if available
  let trueOriginalDimensions = { ...model.originalDimensions };
  
  try {
    const storageKey = `model_dimensions_${model.id}`;
    const savedDataString = localStorage.getItem(storageKey);
    
    if (savedDataString) {
      const savedData = JSON.parse(savedDataString);
      if (savedData && savedData.trueOriginalDimensions) {
        trueOriginalDimensions = savedData.trueOriginalDimensions;
      }
    }
  } catch (e) {
    // Ignore storage errors, use what we have
  }
  
  // Reset to original dimensions
  setDimensions({...trueOriginalDimensions});
  
  // Reset the scale to 1.0
  if (!model.versions) {
    model.versions = [{
      name: 'V1',
      scale: 1.0,
      dimensions: {...trueOriginalDimensions}
    }];
    model.activeVersionIndex = 0;
  } else {
    // Update the current version
    const currentIndex = model.activeVersionIndex || 0;
    model.versions[currentIndex].scale = 1.0;
    model.versions[currentIndex].dimensions = {...trueOriginalDimensions};
  }
  
  // Update the model's originalDimensions to match the true original
  model.originalDimensions = {...trueOriginalDimensions};
};

/**
 * Get the active version name for a model
 * @param model - The model to get the version name for
 * @returns The active version name
 */
export const getActiveVersionName = (model: ModelInfo): string => {
  if (!model.versions || model.activeVersionIndex === undefined) {
    return 'Original';
  }
  
  return model.versions[model.activeVersionIndex].name || 'Version';
}; 